import { AsyncPipe, Location } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DATE_LOCALE, DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { UltimoUsoPipe } from '../../pipes/ultimo-uso.pipe';
import { DateService, MY_DATE_FORMATS } from '../../shared/date.service';
import { Router } from '@angular/router';
import { Observable, startWith, map } from 'rxjs';
import { departamentoValidator, participanteValidator } from '../../commons/reasignar/reasignar.component';
import { ScnackbarData, ToastService, SnackbarStyles } from '../../commons/toast.service';
import { Documento } from '../../models/Document';
import { ServidorPublico } from '../../models/ServidorPublico';
import { AccionTarea } from '../../models/Tarea';
import { UnidadOrganizacional } from '../../models/UnidadOrganizacional';
import { AppTitleService } from '../../services/app-title.service';
import { AuthService } from '../../services/auth.service';
import { DocumentoService } from '../../services/documento.service';
import { ParticipanteService } from '../../services/participante.service';
import { TareaService } from '../../services/tarea.service';
import { UnidadOrganizacionalService } from '../../services/unidad-organizacional.service';
import { UtilService } from '../../services/util.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TableSelectedDocumentsComponent } from "../../commons/table-selected-documents/table-selected-documents.component";
import { TreeAutocompleteComponent } from '../../commons/tree-autocomplete/tree-autocomplete.component';

export type AuxiliarAccionTarea = { departamento: string, participanteID: number, participante: string, fecha: string, comentario: string }

export function participantesValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const participantes: AuxiliarAccionTarea[] = control.value;
    if (!participantes) {
      return { required: true }
    }

    if (participantes.length <= 0) {
      return { requiredOrigen: true }
    }
    return null
  };
}

@Component({
    selector: 'app-direccionar',
    providers: [
        { provide: MAT_DATE_LOCALE, useValue: 'es-EC' },
        {
            provide: DateAdapter,
            useClass: MomentDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
        },
        { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    ],
    imports: [
        MatTableModule,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatDatepickerModule,
        MatAutocompleteModule,
        ReactiveFormsModule,
        AsyncPipe,
        TreeAutocompleteComponent,
        MatIconModule,
        UltimoUsoPipe,
        MatProgressSpinnerModule,
        MatTooltipModule,
        TableSelectedDocumentsComponent
    ], templateUrl: './direccionar.component.html',
    styleUrl: './direccionar.component.scss'
})
export class DireccionarComponent implements OnInit {
  documentIDs: number[]
  currentUserID: string
  utilService = inject(UtilService)

  auxiliarAccionTareas: AuxiliarAccionTarea[] = []
  displayedColumnsParticipants: string[] = ['departamento', 'participante', 'fecha', 'comentario', 'actions'];
  participantsDataSource = new MatTableDataSource<AuxiliarAccionTarea>([])

  documentsInformation: Documento[] = []

  toDirectForm = new FormGroup({
    departamento: new FormControl<UnidadOrganizacional>({ id: "", nombre: "", tipo: 0 }, {
      validators: [departamentoValidator()]
    }),
    participante: new FormControl<string | ServidorPublico>("", {
      validators: [participanteValidator()]
    }),
    maxDate: new FormControl(new Date(), {}),
    comment: new FormControl("", {
      validators: [
        Validators.max(255)
      ]
    }),
    auxiliarAccionTarea: new FormControl<AuxiliarAccionTarea[]>(this.auxiliarAccionTareas, {
      validators: [Validators.required, participantesValidator()]
    }),
  })

  participantes: ServidorPublico[] = []
  participantesFiltered?: Observable<ServidorPublico[]>

  departamentos: UnidadOrganizacional[] = []
  departamentosFiltered?: Observable<UnidadOrganizacional[]>

  fetchingReasignar = false
  snackbarData: ScnackbarData = {}
  minDate = new Date()

  constructor(
    private router: Router,
    private dateService: DateService,
    documentoService: DocumentoService,
    private location: Location,
    private participanteService: ParticipanteService,
    private unidadOrganizacionalService: UnidadOrganizacionalService,
    private tareaService: TareaService,
    private authService: AuthService,
    private toast: ToastService,
  ) {
    inject(AppTitleService).setTitle("Direccionar")

    const navigation = this.router.getCurrentNavigation();
    if (!navigation) {
      location.back()
    }

    this.documentIDs = navigation?.extras.state?.["documentIDs"]

    this.documentIDs.forEach((id) => {
      documentoService.getById(id).subscribe({
        next: (response) => {
          this.documentsInformation = [...this.documentsInformation, response]
        }
      })
    })

    this.currentUserID = this.authService.getTokenData()!.id
  }

  ngOnInit(): void {
    this.unidadOrganizacionalService.getDepartamentosTree().subscribe({
      next: (response) => {
        this.departamentos = response
      }
    })
  }

  onToDirect() {
    this.fetchingReasignar = true
    let accionTareas: AccionTarea[] = []
    this.documentsInformation.forEach(document => {
      this.toDirectForm.controls.auxiliarAccionTarea.value!.forEach(auxiliarAccionTarea => {
        const accionTarea: AccionTarea = {
          id: document.idTarea,
          idParticipante: auxiliarAccionTarea.participanteID,
          fechaExpira: this.dateService.formatDateToCustomStringNoTime(new Date(auxiliarAccionTarea.fecha)),
          comentario: auxiliarAccionTarea.comentario
        }
        accionTareas.push(accionTarea)
      })
    });

    this.tareaService.DireccionarDocumento(accionTareas).subscribe({
      next: (_) => {
        this.snackbarData.message = "Documento direccionado!"
        this.snackbarData.style = SnackbarStyles.Success
        this.snackbarData.iconName = "check_circle"
        this.fetchingReasignar = false
        this.toast.show(this.snackbarData)
        this.utilService.callTasks();
        this.router.navigateByUrl("/enviado")
      },
      error: (_) => {
        this.snackbarData.message = "Error direccionando documento!"
        this.snackbarData.style = SnackbarStyles.Error
        this.snackbarData.iconName = "error"
        this.fetchingReasignar = false
        this.toast.show(this.snackbarData)
      }
    })
  }

  onAddParticipant() {
    if (!this.toDirectForm.controls.participante.value || this.toDirectForm.controls.participante.value == ""){
      return;
    }
    const auxiliarAccionTarea: AuxiliarAccionTarea = {
      departamento: this.toDirectForm.controls.departamento.value!.nombre,
      participanteID: (this.toDirectForm.controls.participante.value as ServidorPublico).id,
      participante: (this.toDirectForm.controls.participante.value as ServidorPublico).nombres + " " + (this.toDirectForm.controls.participante.value as ServidorPublico).apellidos,
      fecha: this.dateService.formatDateToCustomString(this.toDirectForm.controls.maxDate.value!),
      comentario: this.toDirectForm.controls.comment.value!
    }
    if (this.auxiliarAccionTareas.find(participante => participante.participanteID === auxiliarAccionTarea.participanteID)) {
      this.snackbarData.message = "Destinatario ya agregado!"
      this.snackbarData.style = SnackbarStyles.Error
      this.snackbarData.iconName = "error"
      this.toast.show(this.snackbarData)
      return
    }
    this.auxiliarAccionTareas.push(auxiliarAccionTarea)
    this.toDirectForm.controls.auxiliarAccionTarea.setValue(this.auxiliarAccionTareas)
    this.participantsDataSource.data = this.auxiliarAccionTareas.reverse()
  }

  trackDepartamentoChanges(value: any) {
    this.toDirectForm.controls.participante.setValue("")
      if ((value as UnidadOrganizacional).id) {
        this.toDirectForm.controls.departamento.setValue(value)
        this.participanteService.getServidoresPorDepartamento((value as UnidadOrganizacional).id).subscribe({
          next: (response) => {
            response = response.filter(participante => participante.id.toString() !== this.currentUserID);
            if (response.length > 0) {
              response.sort(function (a, b) {
                if (a.apellidos > b.apellidos) { return 1; }
                if (a.apellidos < b.apellidos) { return -1; }
                return 0;
              });
              this.participantes = response
              this.participantesFiltered = this.toDirectForm.controls.participante.valueChanges.pipe(
                startWith(''),
                map(value => {
                  const nombres = typeof value === 'string' ? value : value?.nombres;
                  return nombres ? this._filterParticipante(nombres as string) : this.participantes.slice();
                }),
              );
              this.toDirectForm.controls.participante.enable()
            } else {
              this.snackbarData.message = "Sin usuarios en el departamento!"
              this.snackbarData.style = SnackbarStyles.Error
              this.snackbarData.iconName = "error"
              this.toast.show(this.snackbarData)
            }
          }
        })
      } else {
        this.toDirectForm.controls.departamento.reset()
        this.participantes = []
        this.participantesFiltered = undefined
        this.toDirectForm.controls.participante.setValue(null)
      }
  }

  deleteParticipant(participanteID: number) {
    this.auxiliarAccionTareas = this.auxiliarAccionTareas.filter(participante => participante.participanteID !== participanteID)
    this.toDirectForm.controls.auxiliarAccionTarea.setValue(this.auxiliarAccionTareas)
    this.participantsDataSource.data = this.auxiliarAccionTareas.reverse()
  }

  displayParticipanteFn(participante: ServidorPublico): string {
    return participante && participante.nombres ? participante.nombres.trim() + ' ' + participante.apellidos.trim() : '';
  }

  private _filterParticipante(filter: string): ServidorPublico[] {
    const filterValue = filter.toLowerCase();

    return this.participantes.filter(participante =>
      participante.nombres.toLowerCase().includes(filterValue) ||
      participante.apellidos.toLocaleLowerCase().includes(filterValue) ||
      participante.cargo?.toLowerCase().includes(filterValue) ||
      participante.ciruc.includes(filterValue));
  }


  displayDepartamentoFn(departamento: UnidadOrganizacional): string {
    return departamento && departamento.nombre ? departamento.nombre : '';
  }

  private _filterDepartamento(filter: string): UnidadOrganizacional[] {
    const filterValue = filter.toLowerCase();

    return this.departamentos.filter(departamento =>
      departamento.nombre.toLowerCase().includes(filterValue));
  }

  selectAllText(event: FocusEvent) {
    const input = event.target as HTMLInputElement;
    input.select();
  }

  onCancel() {
    this.location.back()
  }


  formatDate(date: string): string {
    return this.dateService.formatCustomStringToDate(date)
  }
}
